import { useState } from 'react'
import { Info, MailCheck, MailX, ShieldAlert } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { supabase } from '../lib/supabase'
import { assertWrote } from '../data/supabaseAdapter'
import {
  marketingBlocks, type MarketingBlock, type MarketingProfile,
} from '../lib/marketing'
import { Button, Card } from '../ui/kit'

/**
 * Werbung an DIESEN Gast — was erlaubt ist und was widersprochen wurde.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIESE KARTE VERSCHICKT NICHTS MEHR.
 *
 * StaySuite ist am 01.08.2026 aus dem Gastmailversand ausgestiegen (Vorgang 008).
 * Geburtstagsgruß und Wiederkehr-Ansprache gingen bis dahin von hier aus hinaus;
 * geschrieben werden sie künftig im eigenen Mailprogramm des Vermieters.
 *
 * WAS BLEIBT, IST DIE AUSKUNFT — und sie ist der wichtigere Teil. Ob dieser Gast
 * überhaupt angeschrieben werden DARF, entscheidet sich nicht am Sendeweg: eine
 * Werbemail ohne Einwilligung ist auch dann unzulässig, wenn sie aus Outlook kommt.
 * Der Grund steht deshalb weiter in ganzen Sätzen da, und der formlose Widerspruch
 * lässt sich weiter eintragen — er ist eine Pflicht und kein Zusatzkomfort.
 *
 * WAS MIT DEM VERSAND WEGFIEL: die serverseitige Prüfung in `send-guest-mail` und
 * der 300-Tage-Abstand, der gegen den Postausgang zählte. Was hier steht, hat
 * niemanden mehr hinter sich, der es durchsetzt — es erklärt, es sperrt nicht.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export default function GuestMarketingPanel({
  guest, onChanged,
}: {
  guest: MarketingProfile
  /** Nach einer Änderung an der Zeile — der Aufrufer lädt neu. */
  onChanged: () => void
}) {
  const { lang, formatDate } = useI18n()
  const store = useStore()
  const de = lang === 'de'

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const blocks = marketingBlocks(guest)
  const blocked = blocks.length > 0

  /**
   * Der Widerspruch von Hand. Er ist kein Zusatzkomfort, sondern eine Pflicht: der
   * Gast darf formlos widersprechen — mit einer Antwortmail, am Telefon, an der Tür
   * —, und der Betrieb muss das umsetzen. Ohne diesen Knopf gäbe es nur den Weg über
   * den Link in einer Mail, und genau darauf darf man ihn nicht beschränken.
   */
  const unsubscribeByHand = async () => {
    if (!store.live || !supabase) { setError(DEMO_NOTE[de ? 'de' : 'en']); return }
    setBusy(true); setError(null)
    try {
      const now = new Date().toISOString()
      /*
       * Hier zählt die Zeilenzahl mehr als irgendwo sonst in der Anwendung. Ein von
       * RLS abgewiesenes Update meldet keinen Fehler — der Knopf würde also grün
       * quittieren, während die Einwilligung in der Datenbank stehen bleibt und der
       * nächste Geburtstagsgruß an einen Gast geht, der widersprochen hat. Ein
       * Widerspruch, der still verworfen wird, ist kein Anzeigefehler, sondern ein
       * Verstoß: der Betrieb muss ihn umsetzen, und er glaubt, er hätte es getan.
       */
      assertWrote(
        await supabase.from('guests').update({
          marketing_consent: false,
          marketing_unsubscribed_at: now,
          marketing_unsubscribed_source: 'manual',
        }).eq('id', guest.id).select('id'),
        'Widerspruch eintragen',
      )
      onChanged()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="p-3 space-y-3">
      <div className="font-medium flex items-center gap-2">
        <MailCheck size={15} />
        {de ? 'Werbung' : 'Marketing'}
      </div>

      {/* ── Der Zustand, in ganzen Sätzen ──────────────────────────────── */}
      {blocked ? (
        <div className="rounded-xl bg-surface-2 p-3 space-y-1.5">
          <div className="flex items-center gap-2 text-sm font-medium">
            <MailX size={15} className="text-muted shrink-0" />
            {de
              ? 'An diesen Gast darf keine Werbung gehen.'
              : 'No marketing may go to this guest.'}
          </div>
          <p className="text-sm text-muted">{BLOCK_TEXT[blocks[0]!][de ? 'de' : 'en']}</p>
          {blocks.length > 1 && (
            <p className="text-xs text-muted">
              {de ? 'Außerdem: ' : 'Also: '}
              {blocks.slice(1).map((b) => BLOCK_SHORT[b][de ? 'de' : 'en']).join(' · ')}
            </p>
          )}
          {guest.marketing_unsubscribed_at && (
            <p className="text-xs text-muted">
              {de ? 'Widersprochen am ' : 'Objected on '}
              {formatDate(guest.marketing_unsubscribed_at.slice(0, 10))}
              {guest.marketing_unsubscribed_source === 'link'
                ? (de ? ' über den Abmeldelink.' : ' via the unsubscribe link.')
                : (de ? ' — im Betrieb eingetragen.' : ' — recorded by the business.')}
            </p>
          )}
        </div>
      ) : (
        <div className="rounded-xl bg-ok-soft/40 p-3 space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium text-ok">
            <MailCheck size={15} className="shrink-0" />
            {de ? 'Dieser Gast darf Werbung erhalten.' : 'This guest may receive marketing.'}
          </div>
          <p className="text-xs text-muted">
            {de ? 'Einwilligung ' : 'Consent '}
            {guest.marketing_consent_at
              ? formatDate(guest.marketing_consent_at.slice(0, 10))
              : (de ? 'ohne Datum' : 'undated')}
            {guest.marketing_consent_source ? ` · ${guest.marketing_consent_source}` : ''}
          </p>
          {!guest.marketing_consent_at && (
            // Art. 7 Abs. 1: nachweisen können. Ein Häkchen ohne Datum ist kein
            // Nachweis — und der Betrieb soll das sehen, bevor jemand danach fragt.
            <p className="text-xs text-warn">
              {de
                ? 'Ohne Datum und Herkunft lässt sich die Einwilligung nicht nachweisen. Bitte oben ergänzen.'
                : 'Without a date and a source the consent cannot be evidenced.'}
            </p>
          )}
        </div>
      )}

      {/* ── Wer schreibt ───────────────────────────────────────────────── */}
      {!blocked && (
        <p className="text-xs text-muted">
          {de
            ? 'Geburtstagsgruß und Wiederkehr-Ansprache schreiben Sie aus Ihrem eigenen Mailprogramm — StaySuite verschickt keine Gastpost. Bitte selbst darauf achten, dass dieselbe Ansprache nicht zweimal im Jahr hinausgeht und dass ein Hinweis zum Widerspruch mitgeht.'
            : 'You write the birthday greeting and the come-back message from your own mail program — StaySuite sends no guest mail. Please make sure yourself that the same message does not go out twice a year and that a way to object goes with it.'}
        </p>
      )}

      {error && <p className="text-sm text-danger">{error}</p>}

      {/* ── Der formlose Widerspruch ───────────────────────────────────── */}
      {!guest.marketing_unsubscribed_at && !guest.anonymized_at && (
        <div className="pt-1 border-t border-border space-y-1.5">
          <p className="text-xs text-muted flex gap-1.5">
            <Info size={13} className="shrink-0 mt-0.5" />
            {de
              ? 'Ein Gast darf auch formlos widersprechen — mit einer Antwortmail, am Telefon, an der Rezeption. Dann hier eintragen; der Widerspruch gilt dann genauso wie über den Abmeldelink.'
              : 'A guest may object informally too — by reply, by phone, at the desk. Record it here; it counts the same as the unsubscribe link.'}
          </p>
          <Button size="sm" variant="ghost" disabled={busy}
            onClick={() => void unsubscribeByHand()}>
            <MailX size={14} />
            {de ? 'Widerspruch eintragen' : 'Record an objection'}
          </Button>
        </div>
      )}

      {/* ── Was der Betrieb selbst prüfen muss ─────────────────────────── */}
      {store.db.tenant.country === 'AT' && !blocked && (
        // Die ECG-Liste (§ 7 Abs. 2 ECG) wird von der RTR geführt, und ein
        // österreichischer Betrieb hat sie zu beachten. StaySuite fragt sie NICHT
        // ab — das hier zu verschweigen wäre schlimmer, als es dazuzuschreiben.
        <p className="text-xs text-muted flex gap-1.5">
          <ShieldAlert size={13} className="shrink-0 mt-0.5" />
          {de
            ? 'In Österreich führt die RTR die ECG-Liste der Personen, die keine Werbung wünschen (§ 7 Abs. 2 ECG). StaySuite fragt sie nicht ab. Bei einem Gast, der ausdrücklich eingewilligt hat, ist das selten von Belang — geprüft gehört es trotzdem.'
            : 'In Austria the RTR keeps the ECG list of people who do not want advertising. StaySuite does not query it.'}
        </p>
      )}
    </Card>
  )
}

/* ─────────────────────────────────────────────────────── Kleinteile */

/**
 * Warum der Widerspruch in der Demo nicht eingetragen wird. Nicht „Fehler", sondern
 * die Erklärung: er gehört in die Datenbank, und die gibt es hier nicht.
 */
const DEMO_NOTE = {
  de: 'In der Demo wird nichts gespeichert — der Widerspruch gehört an die Zeile des '
    + 'Gasts in der Datenbank, und die gibt es hier nicht.',
  en: 'Nothing is stored in the demo — an objection belongs on the guest’s row in '
    + 'the database, and there is none here.',
}

/** Der ausführliche Grund — der erste Block bestimmt, was der Vermieter liest. */
const BLOCK_TEXT: Record<MarketingBlock, { de: string; en: string }> = {
  anonymized: {
    de: 'Dieser Datensatz ist anonymisiert. Es gibt niemanden mehr, an den geschrieben werden könnte.',
    en: 'This record has been anonymised. There is nobody left to write to.',
  },
  merged: {
    de: 'Dieser Datensatz wurde in einen anderen zusammengeführt. Die Werbung gehört an den übernehmenden Gast.',
    en: 'This record was merged into another one. Write to the surviving guest instead.',
  },
  blacklisted: {
    de: 'Für diesen Gast besteht ein Sperrvermerk. Wer nicht wiederkommen soll, bekommt auch keine Einladung.',
    en: 'This guest is blocked. Somebody you do not want back does not get an invitation.',
  },
  no_email: {
    de: 'Es ist keine E-Mail-Adresse hinterlegt.',
    en: 'No e-mail address on file.',
  },
  unsubscribed: {
    de: 'Der Gast hat der Werbung widersprochen. Das lässt sich nicht von hier aus zurücknehmen — nur der Gast selbst kann neu einwilligen.',
    en: 'The guest objected to marketing. Only the guest can consent again.',
  },
  no_consent: {
    de: 'Der Gast hat nicht in Werbung eingewilligt. Auch ein Geburtstagsgruß ohne jedes Angebot gilt rechtlich als Werbung und braucht deshalb eine Einwilligung. Sie lässt sich oben eintragen, sobald sie vorliegt — mit dem Weg, auf dem sie erteilt wurde.',
    en: 'The guest has not consented to marketing. Even a birthday greeting without any offer counts as advertising in law and needs consent.',
  },
}

/** Die Kurzform für die zweite und dritte Ursache. */
const BLOCK_SHORT: Record<MarketingBlock, { de: string; en: string }> = {
  anonymized: { de: 'anonymisiert', en: 'anonymised' },
  merged: { de: 'zusammengeführt', en: 'merged' },
  blacklisted: { de: 'Sperrvermerk', en: 'blocked' },
  no_email: { de: 'keine E-Mail-Adresse', en: 'no e-mail address' },
  unsubscribed: { de: 'hat widersprochen', en: 'objected' },
  no_consent: { de: 'keine Einwilligung', en: 'no consent' },
}
